import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import DataTable from 'react-data-table-component';
import Button from './Buttons';
import ProgressComponent from './ProgressComponent';
import './Tables.css';
import {CustomSelect} from "./CustomFormElements";

// ✅ Default Expandable Row Component
const DefaultExpandableRow = ({ data }) => (
  <div className='table-folded-content'>
    <div dangerouslySetInnerHTML={{ __html: data.description || '' }} />
  </div>
);

// ✅ Main Table Component
function TableP({
  title,
  columns = [],
  data: propData = [],
  selectableRows = false,
  selectableRowsHighlight = false,
  onSelectedRowsChange,
  expandableRows = false,
  expandableRowsComponent = DefaultExpandableRow,
  expandableRowsComponentProps = {},
  paginationPerPage = 10,
  paginationTotalRows = undefined,
  pagination = true,
  dense = false,
  noTableHead = false,
  disabled = false,
  progressPending = false,
  progressComponent = <ProgressComponent />,
  onRowClicked = () => {},
  onRowDoubleClicked = () => {},
  onRowMouseEnter = () => {},
  onRowMouseLeave = () => {},
  onChangePage = (skip, limit) => {},
  sortIcon,
  onDelete,
  singleSelect = true,
  getRowUrl,
  rowUrlField = 'url',
  onChangeFilter = () => {},
  defaultValue = null,
  typeSearch = null, // 'default' or 'multiselect'
  searchOptions = [],
  headerActions = null,
}) {
  const [selectedRows, setSelectedRows] = useState([]);
  const [toggleClearSelectedRow, setToggleClearSelectedRow] = useState(false);
  const [changePageEvents, setChangePageEvents] = useState([]);

  // Wrapper ref to capture middle clicks
  const tableWrapperRef = useRef(null);

  const customStyles = useMemo(() => ({
    table: { style: { backgroundColor: 'transparent' } },
    tableWrapper: { style: { backgroundColor: 'transparent' } },
    progress: { style: { backgroundColor: 'transparent' } },
  }), []);

  const handleChangePage = (skip, limit) => {
    setChangePageEvents((prevEvents) => [...prevEvents, { skip, limit }]);
    setTimeout(() => {
      setChangePageEvents((pe) => {
        if (pe.length > 0) {
          const e = pe[pe.length - 1];
          console.log('Last event:', e);
          setTimeout(() => {
            onChangePage(e.skip, e.limit);
          }, 10);
        }
        return [];
      });
    }, 10);
  };

  // Handle Row Selection
  const handleRowSelected = ({ selectedRows }) => {
    setSelectedRows(selectedRows);

    // Optional manual highlight (DataTable can already highlight if selectableRowsHighlight=true)
    const allRows = document.querySelectorAll('.rdt_TableRow');
    allRows.forEach((row) => {
      const rowId = row.getAttribute('id');
      const isSelected = selectedRows.some((selectedRow) => `row-${selectedRow.id}` === rowId);
      if (isSelected) row.classList.add('selected');
      else row.classList.remove('selected');
    });

    if (onSelectedRowsChange) onSelectedRowsChange({ selectedRows });
  };

  const handleDelete = useCallback(() => {
    if (selectedRows.length === 0) return;
    const idsToDelete = selectedRows.map((row) => row.id || row.key);
    if (onDelete) {
      onDelete(idsToDelete);
      setToggleClearSelectedRow(!toggleClearSelectedRow);
    }
  }, [selectedRows, onDelete, toggleClearSelectedRow]);

  const contextActions = useMemo(() => (
    <Button onClick={handleDelete} variant="danger" size="medium" disabled={selectedRows.length === 0}>
      Delete Selected
    </Button>
  ), [selectedRows, handleDelete]);

  // ✅ Automatically Detect Expandable Rows (fallback)
  const derivedExpandableRowsComponent = useMemo(() => {
    return ({ data }) =>
      data.description ? (
        <div className='table-folded-content'>
          <div dangerouslySetInnerHTML={{ __html: data.description }} />
        </div>
      ) : null;
  }, []);

  // ---------------------------- Search input for the table header ------------------
    // --- SEARCH state (server-side) ---
  const [searchQuery, setSearchQuery] = useState('');
  const [multiSelectValues, setMultiSelectValues] = useState([]);

  useEffect(() => {
    if (defaultValue) {
      setMultiSelectValues(defaultValue);
    }
  }, [defaultValue]);

  const SearchComponent = typeSearch === 'default' ? (
    <div className='search-wrapper'>
      <div className='icon-wrapper'>
          <i className="fa-solid fa-magnifying-glass"></i>
      </div>
      <input
        aria-label="Search table"
        type="text"
        value={searchQuery}
        onChange={(val) => {
          const newQueryValue = val.target.value;
          setSearchQuery(newQueryValue);
          if (onChangeFilter) {
            onChangeFilter(0, paginationPerPage, newQueryValue);
          }
        }}
        placeholder="Search…"
        className="table-search-input"
      />
      {searchQuery && (
        <button className='table-search_btn' onClick={() => setSearchQuery('')}>
          <i className="fa-solid fa-xmark"></i>
        </button>
      )}
    </div>
  ) : (typeSearch === 'multiselect' && searchOptions && searchOptions.length > 0) ? (
      <div className="input-block d-flex flex-row justify-between" style={{ flex: '0 0 auto', width: 400 }}>
         <CustomSelect
             id="multi-search-select"
             label="Search"
             options={searchOptions.map((item) => ({value: item.value, label: item.label}))}
             value={multiSelectValues}
             multiSelect={true}
             onChange={(val) => {
               setMultiSelectValues(val);
               if (onChangeFilter) {
                 const selectedValues = val.map((v) => v.value || v);
                 onChangeFilter(0, paginationPerPage, selectedValues);
               }
             }}
             placeholder={"Select types"}
         />
       </div>
       ) : null
       // ---------------------------- END OF Search input for the table header ------------------

  // ---------- NEW: header sub-component (search + actions) ----------
  const SubHeader = (
    <div className="d-flex align-items-center" style={{gap: 8}}>
      {typeSearch ? (
        <div>
          {SearchComponent}
        </div>
      ) : null}
      {headerActions ? (
        <div className="d-flex align-items-center">
          {headerActions}
        </div>
      ) : null}
    </div>
  );


  // ---------- NEW: URL resolution helpers ----------
  const resolveRowUrl = useCallback((row) => {
    if (typeof getRowUrl === 'function') return getRowUrl(row);
    if (row && row[rowUrlField]) return row[rowUrlField];
    if (row && row.url) return row.url;
    if (row && row.link) return row.link;
    if (row && row.href) return row.href;
    return null;
  }, [getRowUrl, rowUrlField]);

  const openRowInNewTab = useCallback((row) => {
    const url = resolveRowUrl(row);
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  }, [resolveRowUrl]);

  // ---------- NEW: Middle-click (and Safari fallback) on rows ----------
  useEffect(() => {
    const wrapper = tableWrapperRef.current;
    if (!wrapper) return;

    const handleAuxOrMouseDown = (e) => {
      // React only fires onClick for primary button; we need native events.
      // Only act on middle button.
      if (e.button !== 1) return;

      // Let native <a href> inside cells handle their own middle-clicks
      if (e.target && e.target.closest && e.target.closest('a[href]')) return;

      const rowEl = e.target.closest?.('.rdt_TableRow');
      if (!rowEl) return;

      const body = wrapper.querySelector('.rdt_TableBody');
      if (!body) return;

      const rows = Array.from(body.querySelectorAll('.rdt_TableRow'));
      const idx = rows.indexOf(rowEl);
      if (idx < 0) return;

      const row = propData[idx];
      if (!row) return;

      openRowInNewTab(row);
      e.preventDefault();
    };

    // Modern browsers: 'auxclick' captures middle-clicks reliably
    wrapper.addEventListener('auxclick', handleAuxOrMouseDown);
    // Safari fallback: listen to mousedown
    wrapper.addEventListener('mousedown', handleAuxOrMouseDown);

    return () => {
      wrapper.removeEventListener('auxclick', handleAuxOrMouseDown);
      wrapper.removeEventListener('mousedown', handleAuxOrMouseDown);
    };
  }, [propData, openRowInNewTab]);

  // ---------- BONUS: Ctrl/Cmd + left click opens in new tab ----------
  const handleRowClick = useCallback((row, event) => {
    if (event && (event.metaKey || event.ctrlKey)) {
      openRowInNewTab(row);
      // don't swallow user's own handler
    }
    onRowClicked(row, event);
  }, [openRowInNewTab, onRowClicked]);

  return (
    <div ref={tableWrapperRef}>
      <DataTable
        customStyles={customStyles}
        title={title}
        columns={columns}
        data={propData}
        selectableRows={selectableRows}
        selectableRowsSingle={singleSelect}
        subHeader
        subHeaderComponent={(typeSearch || headerActions) ? SubHeader : null}
        selectableRowsHighlight={selectableRowsHighlight}
        onSelectedRowsChange={handleRowSelected}
        clearSelectedRows={toggleClearSelectedRow}
        contextActions={contextActions}
        responsive
        striped
        highlightOnHover
        pagination={pagination}
        paginationServer
        paginationPerPage={paginationPerPage}
        paginationTotalRows={paginationTotalRows}
        paginationRowsPerPageOptions={[10, 25, 50, 75, 100]}
        expandableRows={expandableRows}
        expandableRowsComponent={expandableRowsComponent || derivedExpandableRowsComponent}
        expandableRowsComponentProps={expandableRowsComponentProps}
        progressPending={progressPending}
        progressComponent={progressComponent}
        dense={dense}
        noTableHead={noTableHead}
        disabled={disabled}
        onRowClicked={handleRowClick}
        onRowDoubleClicked={onRowDoubleClicked}
        onRowMouseEnter={onRowMouseEnter}
        onRowMouseLeave={onRowMouseLeave}
        onChangePage={(page, totalRows) => {
          console.log('Page changed:', page, totalRows, paginationPerPage);
          const skip = (page - 1) * paginationPerPage;
          const limit = paginationPerPage;
          handleChangePage(skip, limit);
        }}
        onChangeRowsPerPage={(currentRowsPerPage, currentPage) => {
          console.log('Rows per page changed:', currentRowsPerPage, currentPage);
          const skip = (currentPage - 1) * currentRowsPerPage;
          const limit = currentRowsPerPage;
          handleChangePage(skip, limit);
        }}
        sortIcon={sortIcon}
        pointerOnHover
      />
    </div>
  );
}

export default TableP;
